import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useMemo } from 'react';
import axios from 'axios';
import '@/App.css';
import '@/index.css';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';

// Pages
import LandingPage from '@/pages/LandingPage';
import VendeurDashboard from '@/pages/VendeurDashboard';
import LivreurDashboard from '@/pages/LivreurDashboard';
import AgentPRDashboard from '@/pages/AgentPRDashboard';
import AdminDashboard from '@/pages/AdminDashboard';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import VerifyEmailPage from '@/pages/VerifyEmailPage';
import PaymentSuccess from '@/pages/PaymentSuccess';
import AffiliateDashboard from '@/pages/AffiliateDashboard';
import AmbassadorProgramPage from '@/pages/AmbassadorProgramPage';

// New Public Pages
import TrackingPageNew from '@/pages/TrackingPageNew';
import TrackingEmailActionPage from '@/pages/TrackingEmailActionPage';
import AboutPage from '@/pages/institutional/AboutPage';
import FAQPage from '@/pages/institutional/FAQPage';
import ContactPage from '@/pages/institutional/ContactPage';
import PricingPage from '@/pages/PricingPage';
import ServicesPage from '@/pages/ServicesPage';
import IntegrationsPage from '@/pages/IntegrationsPage';
import BlogPage from '@/pages/BlogPage';
import BlogPostPage from '@/pages/BlogPostPage';
import PublicVendorsPage from '@/pages/PublicVendorsPage';
import VendorReviewPage from '@/pages/VendorReviewPage';
import DeliveryReviewPage from '@/pages/DeliveryReviewPage';
import NewsletterUnsubscribePage from '@/pages/NewsletterUnsubscribePage';
import NotificationPreferencesPage from '@/pages/account/NotificationPreferencesPage';
import SavedAddressesPage from '@/pages/account/SavedAddressesPage';
import AccountSettingsPage from '@/pages/account/AccountSettingsPage';

// Legal Pages
import TermsPage from '@/pages/legal/TermsPage';
import PrivacyPage from '@/pages/legal/PrivacyPage';
import CookiesPage from '@/pages/legal/CookiesPage';
import PrivacySettingsPage from '@/pages/legal/PrivacySettingsPage';
import MentionsPage from '@/pages/legal/MentionsPage';

// Components
import DynamicBanner from '@/components/shared/DynamicBanner';
import EmailVerificationBanner from '@/components/shared/EmailVerificationBanner';
import { usePublicSeo, PUBLIC_SITE_URL, resolveAbsoluteUrl } from '@/utils/seo';
import { installGoogleAnalytics, shouldTrackPath, trackPageView } from '@/utils/analytics';
import { clearAuthSession, getAuthToken, getRefreshToken, persistAuthSession } from '@/utils/authSession';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const normalizeUserRole = (role) => {
  const normalizedRole = String(role || '').trim().toLowerCase();
  if (normalizedRole === 'courier') return 'livreur';
  if (normalizedRole === 'vendor') return 'vendeur';
  if (normalizedRole === 'agent-pr' || normalizedRole === 'agentpr' || normalizedRole === 'relay_agent') return 'agent_pr';
  if (normalizedRole === 'affiliate') return 'ambassador';
  return normalizedRole;
};

// Axios interceptor for auth token
axios.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let refreshRequest = null;
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error?.config;
    const refreshToken = getRefreshToken();
    if (error?.response?.status !== 401 || !refreshToken || originalRequest?._authRetried || String(originalRequest?.url || '').includes('/auth/refresh')) {
      return Promise.reject(error);
    }
    originalRequest._authRetried = true;
    try {
      refreshRequest ||= axios.post(`${API}/auth/refresh`, { refresh_token: refreshToken });
      const refreshed = await refreshRequest;
      persistAuthSession(refreshed.data);
      originalRequest.headers = originalRequest.headers || {};
      originalRequest.headers.Authorization = `Bearer ${refreshed.data.access_token || refreshed.data.token}`;
      return axios(originalRequest);
    } catch (refreshError) {
      clearAuthSession();
      return Promise.reject(refreshError);
    } finally {
      refreshRequest = null;
    }
  },
);

const ProtectedRoute = ({ children, allowedRoles }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    const checkAuth = async () => {
      const token = getAuthToken();
      if (!token) {
        if (active) {
          setUser(null);
          setLoading(false);
        }
        return;
      }

      try {
        const response = await axios.get(`${API}/auth/me`);
        if (active) {
          setUser({ ...response.data, role: normalizeUserRole(response.data?.role) });
        }
      } catch (error) {
        clearAuthSession();
        if (active) {
          setUser(null);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    checkAuth();

    const refreshAuth = () => {
      checkAuth();
    };

    window.addEventListener('focus', refreshAuth);
    window.addEventListener('cnk-auth-refresh', refreshAuth);

    return () => {
      active = false;
      window.removeEventListener('focus', refreshAuth);
      window.removeEventListener('cnk-auth-refresh', refreshAuth);
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-cyan-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-red-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" />;
  }

  if (allowedRoles && !allowedRoles.map(normalizeUserRole).includes(normalizeUserRole(user.role))) {
    return <Navigate to="/" />;
  }

  return (
    <>
      <EmailVerificationBanner
        user={user}
        onUserUpdate={(nextUser) => setUser(nextUser ? { ...nextUser, role: normalizeUserRole(nextUser?.role) } : nextUser)}
      />
      {children}
    </>
  );
};

const PRIVATE_ROUTE_PREFIXES = ['/admin', '/vendeur', '/livreur', '/agent-pr', '/affiliate/dashboard', '/account'];
const NOINDEX_ROUTE_PREFIXES = [
  ...PRIVATE_ROUTE_PREFIXES,
  '/reset-password',
  '/forgot-password',
  '/verify-email',
  '/payment/success',
  '/tracking/confirm-email',
  '/track/confirm-email',
  '/tracking/unsubscribe-email',
  '/track/unsubscribe-email',
  '/newsletter/unsubscribe',
  '/review/',
  '/vendors/',
];

const ORGANIZATION_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': `${PUBLIC_SITE_URL}/#organization`,
  name: 'Coli Noki',
  url: PUBLIC_SITE_URL,
  logo: resolveAbsoluteUrl('/favicon.ico'),
  email: 'contact@colinoki.com',
  telephone: ['+242044160008', '+242064764582'],
  areaServed: [
    { '@type': 'Country', name: 'République du Congo' },
    { '@type': 'Country', name: 'République démocratique du Congo' },
    { '@type': 'Place', name: 'Afrique centrale' },
  ],
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer support',
    telephone: '+242044160008',
    email: 'support@colinoki.com',
    availableLanguage: ['fr'],
  },
};

const WEBSITE_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${PUBLIC_SITE_URL}/#website`,
  name: 'Coli Noki',
  url: PUBLIC_SITE_URL,
  inLanguage: 'fr',
  publisher: { '@id': `${PUBLIC_SITE_URL}/#organization` },
  potentialAction: {
    '@type': 'SearchAction',
    target: `${PUBLIC_SITE_URL}/track?code={tracking_code}`,
    'query-input': 'required name=tracking_code',
  },
};

const DELIVERY_SERVICE_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'DeliveryService',
  '@id': `${PUBLIC_SITE_URL}/#delivery-service`,
  name: 'Coli Noki',
  url: PUBLIC_SITE_URL,
  provider: { '@id': `${PUBLIC_SITE_URL}/#organization` },
  areaServed: ['Brazzaville', 'Pointe-Noire', 'Kinshasa', 'Congo', 'Afrique centrale'],
  serviceType: 'Livraison, suivi colis et gestion logistique',
  description:
    'Coli Noki est une plateforme moderne de livraison, suivi colis et gestion logistique pour vendeurs, e-commerce et particuliers au Congo et en Afrique centrale.',
};

const SERVICE_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  '@id': `${PUBLIC_SITE_URL}/#service`,
  name: 'Plateforme Coli Noki',
  provider: { '@id': `${PUBLIC_SITE_URL}/#organization` },
  serviceType: 'Gestion de livraison et suivi de colis',
  areaServed: ['Congo', 'Afrique centrale'],
  description:
    'Création de livraisons, suivi de colis, options express, assurance, intégrations e-commerce et outils de gestion pour vendeurs, e-commerce et particuliers.',
};

const BASE_STRUCTURED_DATA = [
  { id: 'organization', data: ORGANIZATION_SCHEMA },
  { id: 'website', data: WEBSITE_SCHEMA },
  { id: 'delivery-service', data: DELIVERY_SERVICE_SCHEMA },
  { id: 'service', data: SERVICE_SCHEMA },
];

const CORE_STRUCTURED_DATA = [
  { id: 'organization', data: ORGANIZATION_SCHEMA },
  { id: 'website', data: WEBSITE_SCHEMA },
];

const ScrollManager = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      window.setTimeout(() => {
        const target = document.getElementById(location.hash.slice(1));
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
          window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        }
      }, 0);
      return;
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname, location.search, location.hash]);

  return null;
};

const GoogleAnalyticsRouter = () => {
  const location = useLocation();

  useEffect(() => {
    installGoogleAnalytics();
  }, []);

  useEffect(() => {
    const path = `${location.pathname}${location.search}${location.hash}`;
    if (shouldTrackPath(location.pathname)) {
      trackPageView(path);
    }
  }, [location.pathname, location.search, location.hash]);

  return null;
};

const RouteIndexingGuard = () => {
  const location = useLocation();
  const isNoindexRoute = NOINDEX_ROUTE_PREFIXES.some((prefix) => location.pathname.startsWith(prefix));
  const structuredData = useMemo(() => {
    if (isNoindexRoute) return [];
    return location.pathname === '/services' ? CORE_STRUCTURED_DATA : BASE_STRUCTURED_DATA;
  }, [isNoindexRoute, location.pathname]);

  const publicMeta = {
    '/': {
      title: 'Coli Noki | Livraison, suivi de colis et gestion vendeur',
      description:
        'Plateforme Coli Noki pour créer des livraisons, suivre les colis et accéder aux services standard, express et assurance.',
    },
    '/register': {
      title: 'Inscription vendeur | Coli Noki',
      description:
        'Inscrivez votre boutique sur Coli Noki, avec prise en charge affiliation et accès aux services de livraison.',
    },
    '/pricing': {
      title: 'Tarifs Coli Noki | Free vs Coli Noki Pro',
      description:
        'Comparez les plans Gratuit et Coli Noki Pro : livraison express, assurance colis, intégrations, portefeuille avancé et affiliation.',
    },
    '/blog': {
      title: 'Blog Coli Noki | Livraison, e-commerce et logistique',
      description:
        'Articles Coli Noki sur la logistique, la livraison, le e-commerce, la relation client et les bonnes pratiques vendeur.',
    },
    '/services': {
      title: 'Nos services | Livraison, suivi colis et logistique au Congo',
      description:
        'Découvrez les services Coli Noki : livraison locale à Brazzaville, livraison e-commerce, express, suivi colis et solutions logistiques au Congo.',
    },
    '/about': {
      title: 'À propos de Coli Noki | Livraison et logistique en Afrique centrale',
      description:
        'Découvrez Coli Noki, plateforme moderne de livraison, suivi colis et gestion logistique pour vendeurs, e-commerce et particuliers.',
    },
    '/contact': {
      title: 'Contact Coli Noki | Support livraison et vendeurs',
      description:
        'Contactez Coli Noki pour une question sur la livraison, le suivi colis, les comptes vendeurs, les partenariats ou le support.',
    },
    '/integrations': {
      title: 'Intégrations e-commerce | Coli Noki',
      description:
        'Connectez Shopify, WooCommerce ou votre propre système à Coli Noki pour automatiser vos commandes et vos livraisons.',
    },
    '/ambassador-program': {
      title: 'Programme AMBASSADEUR | Coli Noki',
      description:
        'Devenez AMBASSADEUR Coli Noki : candidature publique, validation admin, commissions sur abonnements Pro et tableau de bord dédié.',
    },
    '/ambassadeur': {
      title: 'Programme AMBASSADEUR | Coli Noki',
      description:
        'Devenez AMBASSADEUR Coli Noki : candidature publique, validation admin, commissions sur abonnements Pro et tableau de bord dédié.',
      canonicalPath: '/ambassador-program',
    },
    '/vendors': {
      title: 'Vendeurs vérifiés | Coli Noki',
      description: 'Consultez la liste publique des vendeurs vérifiés, badges de confiance et avis clients.',
    },
    '/vendeurs': {
      title: 'Vendeurs vérifiés | Coli Noki',
      description: 'Consultez la liste publique des vendeurs vérifiés, badges de confiance et avis clients.',
      canonicalPath: '/vendors',
    },
    '/track': {
      title: 'Suivi de colis | Coli Noki',
      description: 'Suivez votre colis Coli Noki en temps réel avec votre code de suivi et les statuts de livraison.',
    },
    '/tracking': {
      title: 'Suivi de colis | Coli Noki',
      description: 'Suivez votre colis Coli Noki en temps réel avec votre code de suivi et les statuts de livraison.',
      canonicalPath: '/track',
    },
    '/tracking/confirm-email': {
      title: 'Confirmation suivi email | Coli Noki',
      description: 'Confirmez votre abonnement aux mises a jour email de suivi colis Coli Noki.',
    },
    '/track/confirm-email': {
      title: 'Confirmation suivi email | Coli Noki',
      description: 'Confirmez votre abonnement aux mises a jour email de suivi colis Coli Noki.',
      canonicalPath: '/tracking/confirm-email',
    },
    '/tracking/unsubscribe-email': {
      title: 'Desabonnement suivi email | Coli Noki',
      description: 'Desactivez les mises a jour email de suivi colis Coli Noki.',
    },
    '/track/unsubscribe-email': {
      title: 'Desabonnement suivi email | Coli Noki',
      description: 'Desactivez les mises a jour email de suivi colis Coli Noki.',
      canonicalPath: '/tracking/unsubscribe-email',
    },
    '/newsletter/unsubscribe': {
      title: 'Désinscription newsletter | Coli Noki',
      description: 'Gérez votre abonnement aux communications marketing Coli Noki.',
    },
    '/verify-email': {
      title: 'Verification email | Coli Noki',
      description: 'Confirmez votre adresse email Coli Noki avec un lien securise et a expiration.',
    },
    '/faq': {
      title: 'FAQ Coli Noki | Aide livraison, portefeuille, affiliation',
      description: 'Retrouvez les réponses sur les livraisons, Coli Noki Pro, l’assurance, le portefeuille, le suivi de colis, le KYC et l’affiliation.',
    },
    '/privacy': {
      title: 'Politique de confidentialité | Coli Noki',
      description: 'Détails sur les données collectées, paiements, KYC, affiliation, newsletter et services Stripe, Mailgun et Brevo.',
    },
    '/legal/privacy': {
      title: 'Politique de confidentialité | Coli Noki',
      description: 'Détails sur les données collectées, paiements, KYC, affiliation, newsletter et services Stripe, Mailgun et Brevo.',
    },
    '/politique-confidentialite': {
      title: 'Politique de confidentialité | Coli Noki',
      description: 'Détails sur les données collectées, paiements, KYC, affiliation, newsletter et services Stripe, Mailgun et Brevo.',
    },
    '/terms': {
      title: "Conditions d'utilisation | Coli Noki",
      description: 'Règles de responsabilité vendeur/livreur, service express, assurance colis, portefeuille et affiliation.',
    },
    '/legal/terms': {
      title: "Conditions d'utilisation | Coli Noki",
      description: 'Règles de responsabilité vendeur/livreur, service express, assurance colis, portefeuille et affiliation.',
    },
    '/mentions-legales': {
      title: 'Mentions légales | Coli Noki',
      description: 'Informations légales sur la plateforme Coli Noki.',
    },
    '/legal/mentions': {
      title: 'Mentions légales | Coli Noki',
      description: 'Informations légales sur la plateforme Coli Noki.',
    },
    '/legal/cookies': {
      title: 'Politique des cookies | Coli Noki',
      description: 'Informations sur les cookies et préférences de confidentialité de Coli Noki.',
    },
    '/legal/privacy-settings': {
      title: 'Préférences de confidentialité | Coli Noki',
      description: 'Gérez vos préférences de confidentialité et de cookies pour les services Coli Noki.',
    },
  };

  const routeMeta = publicMeta[location.pathname] || {
    title: 'Coli Noki | Plateforme de livraison',
    description: 'Coli Noki facilite la création, le suivi et la gestion des livraisons.',
  };

  usePublicSeo({
    ...routeMeta,
    path: routeMeta.canonicalPath || location.pathname,
    robots: isNoindexRoute ? 'noindex,nofollow' : 'index,follow',
    structuredData,
  });

  return null;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        {/* Dynamic Banner */}
        <DynamicBanner page="home" />
        <ScrollManager />
        <RouteIndexingGuard />
        <GoogleAnalyticsRouter />
        
        <Routes>
          {/* Home */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/register" element={<LandingPage />} />
          
          {/* Public Pages */}
          <Route path="/track" element={<TrackingPageNew />} />
          <Route path="/tracking" element={<TrackingPageNew />} />
          <Route path="/tracking/confirm-email" element={<TrackingEmailActionPage mode="confirm" />} />
          <Route path="/track/confirm-email" element={<TrackingEmailActionPage mode="confirm" />} />
          <Route path="/tracking/unsubscribe-email" element={<TrackingEmailActionPage mode="unsubscribe" />} />
          <Route path="/track/unsubscribe-email" element={<TrackingEmailActionPage mode="unsubscribe" />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/integrations" element={<IntegrationsPage />} />
          <Route path="/ambassador-program" element={<AmbassadorProgramPage />} />
          <Route path="/ambassadeur" element={<AmbassadorProgramPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/vendors" element={<PublicVendorsPage />} />
          <Route path="/vendeurs" element={<PublicVendorsPage />} />
          <Route path="/vendors/:vendorId/review" element={<VendorReviewPage />} />
          <Route path="/review/:token" element={<DeliveryReviewPage />} />
          <Route path="/newsletter/unsubscribe" element={<NewsletterUnsubscribePage />} />
          
          {/* Auth Pages */}
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          
          {/* Legal Pages */}
          <Route path="/legal/terms" element={<TermsPage />} />
          <Route path="/legal/privacy" element={<PrivacyPage />} />
          <Route path="/legal/cookies" element={<CookiesPage />} />
          <Route path="/legal/privacy-settings" element={<PrivacySettingsPage />} />
          <Route path="/legal/mentions" element={<MentionsPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          
          {/* Legacy routes (for backwards compatibility) */}
          <Route path="/mentions-legales" element={<MentionsPage />} />
          <Route path="/politique-confidentialite" element={<PrivacyPage />} />
          
          {/* Protected Routes */}
          <Route
            path="/vendeur/*"
            element={
              <ProtectedRoute allowedRoles={['vendeur']}>
                <VendeurDashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/affiliate/dashboard"
            element={
              <ProtectedRoute allowedRoles={['vendeur', 'ambassador', 'super_ambassador']}>
                <AffiliateDashboard />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/livreur/*"
            element={
              <ProtectedRoute allowedRoles={['livreur']}>
                <LivreurDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/account/settings"
            element={
              <ProtectedRoute>
                <AccountSettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/account/notifications"
            element={
              <ProtectedRoute>
                <NotificationPreferencesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/account/addresses"
            element={
              <ProtectedRoute>
                <SavedAddressesPage />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/agent-pr/*"
            element={
              <ProtectedRoute allowedRoles={['agent_pr']}>
                <AgentPRDashboard />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/*"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
        </Routes>
        <Toaster position="top-right" />
      </BrowserRouter>
    </div>
  );
}

export default App;
export { API, toast };

